import React, { useEffect, useState } from 'react';
import { walletAPI } from '@/services/api';
import { paymentsAPI } from '@/services/paymentsAPI';
import ScrollHint from '@/components/shared/ScrollHint';
import RazorpayCheckoutModal from '@/components/shared/RazorpayCheckoutModal';
import { useAuthStore } from '@/store/authStore';
import { Wallet, ArrowDown, ArrowUp, Plus, CreditCard, IndianRupee } from 'lucide-react';

export default function StaffingWallet() {
  const { user } = useAuthStore();
  const [w, setW] = useState(null);
  const [txns, setTxns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [rechargeOpen, setRechargeOpen] = useState(false);

  const load = async () => {
    try { setW(await walletAPI.me()); } catch { /* */ }
    try { const d = await walletAPI.myTransactions({ limit: 50 }); setTxns(d.transactions || d.rows || []); } catch { /* */ }
    try { const h = await paymentsAPI.getHistory(); setPayments(h.records || []); } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="staffing-wallet">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-6 flex items-center gap-2"><Wallet className="w-5 h-5" /> Wallet</h1>
      <div className="bg-gradient-to-br from-emerald-500/10 via-white dark:via-[#111114] to-white dark:to-[#111114] border border-emerald-500/20 rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider mb-1">Current Balance</p>
            <p className={`text-4xl font-semibold ${w?.balance >= 0 ? 'text-slate-900 dark:text-white' : 'text-red-600 dark:text-red-400'}`} data-testid="wallet-balance">{w?.balance ?? '—'} <span className="text-base text-slate-500 dark:text-zinc-500">MP</span></p>
            {w?.balance < 0 && <p className="text-red-700 dark:text-red-300 text-xs mt-2">You have a negative balance — please contact Numuv admin or top up to clear the debt.</p>}
          </div>
          <button
            onClick={() => setRechargeOpen(true)}
            className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium flex items-center gap-1 shadow"
            data-testid="wallet-recharge-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Recharge
          </button>
        </div>
      </div>

      <ScrollHint>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-xl overflow-x-auto">
        <p className="text-slate-900 dark:text-white text-sm font-medium px-5 py-4 border-b border-slate-200 dark:border-zinc-800/50">Transaction History</p>
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-white dark:bg-[#0e0e10] text-slate-500 dark:text-zinc-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left p-3">Type</th><th className="text-left p-3">Reason</th><th className="text-right p-3">Amount</th><th className="text-right p-3">Balance</th><th className="text-right p-3">When</th></tr>
          </thead>
          <tbody>
            {txns.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500 dark:text-zinc-500">No transactions yet.</td></tr>}
            {txns.map((t, i) => (
              <tr key={i} className="border-t border-slate-200 dark:border-zinc-800/50">
                <td className="p-3">
                  {t.kind === 'credit'
                    ? <span className="text-emerald-700 dark:text-emerald-300 text-xs flex items-center gap-1"><ArrowDown className="w-3 h-3" /> CREDIT</span>
                    : <span className="text-red-700 dark:text-red-300 text-xs flex items-center gap-1"><ArrowUp className="w-3 h-3" /> DEBIT</span>}
                </td>
                <td className="p-3 text-slate-500 dark:text-zinc-400 text-xs">{t.reason}</td>
                <td className={`p-3 text-right ${t.kind === 'credit' ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>{t.kind === 'credit' ? '+' : '-'}{t.amount}</td>
                <td className="p-3 text-right text-slate-600 dark:text-zinc-300">{t.balance_after}</td>
                <td className="p-3 text-right text-slate-500 dark:text-zinc-500 text-xs">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </ScrollHint>

      {payments.length > 0 && (
        <section className="mt-6" data-testid="staffing-payments-section">
          <h2 className="text-sm font-semibold text-slate-600 dark:text-zinc-300 uppercase tracking-wider mb-2 flex items-center gap-2">
            <CreditCard className="w-3.5 h-3.5" /> Razorpay Recharge History
          </h2>
          <ScrollHint>
            <div className="overflow-x-auto bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg">
              <table className="w-full text-xs min-w-[720px]" data-testid="staffing-payments-table">
                <thead className="border-b border-slate-200 dark:border-zinc-800/70">
                  <tr className="text-slate-500 dark:text-zinc-500 text-left">
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Amount paid</th>
                    <th className="px-3 py-2 font-medium">Mu credited</th>
                    <th className="px-3 py-2 font-medium">Rate</th>
                    <th className="px-3 py-2 font-medium">Razorpay Payment ID</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-slate-200 dark:border-zinc-900/60" data-testid={`staffing-payment-row-${p.id}`}>
                      <td className="px-3 py-2 text-slate-500 dark:text-zinc-400 whitespace-nowrap">
                        {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-900 dark:text-white font-medium flex items-center gap-0.5">
                        <IndianRupee className="w-3 h-3" />{p.amount_inr}
                      </td>
                      <td className="px-3 py-2 text-emerald-600 dark:text-emerald-400 font-medium">+{p.mu_points_credited} MP</td>
                      <td className="px-3 py-2 text-slate-600 dark:text-zinc-300">₹{p.conversion_rate_inr_per_mu}/MP</td>
                      <td className="px-3 py-2 text-slate-600 dark:text-zinc-400 font-mono text-[11px] truncate max-w-[220px]" title={p.razorpay_payment_id}>{p.razorpay_payment_id}</td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                          {p.status || 'success'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ScrollHint>
        </section>
      )}

      <RazorpayCheckoutModal
        isOpen={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        user={{ email: user?.email, phone: user?.phone, name: user?.name }}
        onSuccess={() => load()}
      />
    </div>
  );
}
